/** @format */
import angular from 'angular';
import _ from 'lodash';
import { authModule } from 'sis-common';
import { errorServiceModule } from '../error-handler/legacy/errorService';
import { IconComponent } from '../icon/icon.component';
import { publicPersonModuleModel } from '../model/publicPerson.model';
import { PaginationComponent } from '../pagination/pagination.component';
import { scrollIntoViewOnPageChangeModule } from '../scroll/scrollIntoViewOnPageChange.directive';
import { commonCourseUnitServiceModule } from '../service/courseUnit.service';
import { jsDataRelationHelperModule } from '../service/jsDataRelationHelper.service';
import { commonModuleServiceModule } from '../service/module.service';
import { commonStudentApplicationServiceModule } from '../service/studentApplication.service';
import { localizedStringModule } from '../string/localizedString.dir';
import studentApplicationListTpl from './studentApplicationList.tpl.html';

export const studentApplicationListModule = 'sis-components.applications.studentApplicationList';

(function () {
    studentApplicationListController.$inject = [
        '$translate',
        'localizedStringFilter',
        'publicPersonModel',
        'jsDataCacheHelper',
        'AuthService',
        'commonStudentApplicationService',
        'defaultPromiseHandler',
        'confirmDialogService',
    ];
    /**
     * This component should no longer be used in new feature development. Use the Angular
     * StudentWorkflowListComponent instead.
     *
     * @deprecated
     */
    angular
        .module(studentApplicationListModule, [
            commonStudentApplicationServiceModule,
            commonCourseUnitServiceModule,
            commonModuleServiceModule,
            IconComponent.downgrade.moduleName,
            PaginationComponent.downgrade.moduleName,
            localizedStringModule,
            jsDataRelationHelperModule,
            publicPersonModuleModel,
            authModule,
            errorServiceModule,
            scrollIntoViewOnPageChangeModule,
        ])
        .component('studentApplicationList', {
            template: studentApplicationListTpl,
            controller: studentApplicationListController,
            bindings: {
                studentApplicationSearchResult: '<',
                columns: '<',
                reverse: '<?',
                sortColumn: '<',
                sortColumns: '<',
                studentPageView: '<',
                navigateToApplication: '<',
                setSortAndPagination: '<?',
            },
        });

    function studentApplicationListController(
        $translate,
        localizedStringFilter,
        publicPersonModel,
        jsDataCacheHelper,
        AuthService,
        commonStudentApplicationService,
        defaultPromiseHandler,
        confirmDialogService,
    ) {
        // NOSONAR
        const $ctrl = this;
        $ctrl.maxSize = 5;

        $ctrl.$onInit = function () {
            $ctrl.currentPage = 1;
            $ctrl.reverse = !!$ctrl.reverse;
            $ctrl.isOnStudentPage = !!$ctrl.studentPageView;
            $ctrl.userId = AuthService.personId();
        };

        $ctrl.setOrder = function (sortColumn) {
            $ctrl.reverse = $ctrl.sortColumn === sortColumn ? !$ctrl.reverse : false;
            $ctrl.sortColumn = sortColumn;
            $ctrl.onPageChange($ctrl.currentPage);
        };

        $ctrl.isSortColumn = function (columnName) {
            return _.includes($ctrl.sortColumns, columnName);
        };

        $ctrl.$onChanges = function () {
            $ctrl.totalItems = _.get($ctrl.studentApplicationSearchResult, 'total', 0);
            $ctrl.itemsPerPage = _.get($ctrl.studentApplicationSearchResult, 'limit', 20);
            $ctrl.currentPage = _.get($ctrl.studentApplicationSearchResult, 'start', 0) / $ctrl.itemsPerPage + 1;

            setLastHandlerNames(_.filter(_.get($ctrl.studentApplicationSearchResult, 'searchResults', []), 'lastHandlerPersonId'));
        };

        $ctrl.lastHandlerNames = {};

        function setLastHandlerNames(applications) {
            const lastHandlerPersonIds = _.map(applications, 'lastHandlerPersonId');
            jsDataCacheHelper
                .findByIds(publicPersonModel, lastHandlerPersonIds)
                .then(lastHandlerPersons => {
                    _.forEach(applications, application => {
                        const lastHandler = _.find(lastHandlerPersons, { id: application.lastHandlerPersonId });
                        $ctrl.lastHandlerNames[application.id] = _.compact([
                            lastHandler.lastName,
                            lastHandler.firstNames || lastHandler.firstName,
                        ]).join(', ');
                    });
                })
                .catch(defaultPromiseHandler.loggingRejectedPromiseHandler);
        }

        $ctrl.onPageChange = function (newPage) {
            $ctrl.currentPage = newPage;
            const start = ($ctrl.currentPage - 1) * $ctrl.itemsPerPage;
            $ctrl.setSortAndPagination(start, $ctrl.itemsPerPage, $ctrl.sortColumn, $ctrl.reverse);
        };

        $ctrl.getApplicationName = function (application) {
            if (application.name) {
                return application.name;
            }
            if (application.courseUnit) {
                return localizedStringFilter(application.courseUnit.name);
            }
            if (application.module) {
                return localizedStringFilter(application.module.name);
            }
            if (application.education) {
                return localizedStringFilter(application.education.name);
            }
            return '-';
        };

        $ctrl.getStudentName = function (application) {
            return _.compact([application.personLastName, application.personFirstNames]).join(', ');
        };

        $ctrl.getStatusIcon = function (state) {
            switch (state) {
                case 'REQUESTED':
                case 'IN_HANDLING':
                    return 'hourglass';
                case 'REJECTED':
                case 'CANCELLED':
                case 'REVOKED':
                case 'ACCEPTANCE_REVOKED':
                    return 'fail';
                case 'ACCEPTED':
                case 'CONDITIONAL':
                    return 'check';
                default:
                    return 'hourglass';
            }
        };

        $ctrl.getStatusIconClass = function (state) {
            switch (state) {
                case 'REQUESTED':
                case 'IN_HANDLING':
                case 'REJECTED':
                case 'ACCEPTED':
                case 'CONDITIONAL':
                    return null;
                case 'CANCELLED':
                case 'REVOKED':
                case 'ACCEPTANCE_REVOKED':
                    return 'cancelled';
                default:
                    return null;
            }
        };
        $ctrl.getAriaSortValue = function (state) {
            if ($ctrl.sortColumn === state) {
                if ($ctrl.reverse) {
                    return 'descending';
                }
                return 'ascending';
            }
            return 'none';
        };

        $ctrl.getSorterClass = function (state) {
            if ($ctrl.sortColumn === state && !$ctrl.reverse) {
                return null;
            }
            if ($ctrl.sortColumn === state && !!$ctrl.reverse) {
                return 'flip-180';
            }
            return 'light';
        };

        $ctrl.replaceUpdatedApplication = function (application) {
            const searchResults = _.get($ctrl.studentApplicationSearchResult, 'searchResults', []);
            const searchResult = _.find(searchResults, { id: application.id });
            const fieldsToBeReplaced = _.pick(application, ['lastHandlerPersonId', 'lastHandledTime', 'state']);
            _.assign(searchResult, fieldsToBeReplaced);
            setLastHandlerNames(_.filter(searchResults, 'lastHandlerPersonId'));
        };

        $ctrl.setMeAsLastHandler = function (application) {
            commonStudentApplicationService
                .updateLastHandler(application, $ctrl.userId)
                .then(response => {
                    $ctrl.replaceUpdatedApplication(response);
                })
                .catch(defaultPromiseHandler.loggingRejectedPromiseHandler);
        };

        const removeLastHandler = application => {
            commonStudentApplicationService
                .removeLastHandler(application)
                .then(response => {
                    $ctrl.replaceUpdatedApplication(response);
                })
                .catch(defaultPromiseHandler.loggingRejectedPromiseHandler);
        };

        const removeLastHandlerPrompt = application => {
            const confirmTexts = {
                title: 'STUDENT_APPLICATIONS.APPLICATION_HANDLER.MODAL.TITLE',
                description: 'STUDENT_APPLICATIONS.APPLICATION_HANDLER.MODAL.DESCRIPTION',
                cancelText: 'STUDENT_APPLICATIONS.APPLICATION_HANDLER.MODAL.CANCEL',
                confirmText: 'STUDENT_APPLICATIONS.APPLICATION_HANDLER.MODAL.CONFIRM',
            };
            confirmDialogService
                .confirm(confirmTexts)
                .then(() => removeLastHandler(application))
                .catch(defaultPromiseHandler.loggingRejectedPromiseHandler);
        };

        $ctrl.removeHandler = function (application) {
            if (application.lastHandlerPersonId === $ctrl.userId) {
                removeLastHandler(application);
            } else {
                removeLastHandlerPrompt(application);
            }
        };
    }
})();
